/**
 * `/settings/data` — where the founder can leave (§3, §4.10, §11 Phase 0).
 *
 * FRONTEND_DESIGN §10.4 names this as one of the things the founder has to be
 * able to do without developer help: "stop early, export data, and find
 * complete deletion". That is the whole reason this screen is Phase 0 work
 * rather than something added once there is data worth exporting — a system
 * you cannot leave is a different kind of system, and the time to find out
 * whether you can leave is before there is anything to lose.
 *
 * Deliberately out of scope while D2 holds (all inference is local): §4.10's
 * privacy-minimised export, its two source-inclusion options, and
 * `/settings/model`. The first two would filter text that does not exist
 * before Phase 2, and the third describes a consent boundary nothing currently
 * crosses. Offering an option that filters nothing would be a claim about the
 * export that is not yet true.
 *
 * The screen fetches nothing on mount, and that is a decision. The obvious
 * thing to load would be the data root's path, and the only endpoint that
 * reports it is the deletion preview — which mints a short-lived deletion
 * token as a side effect. Opening a settings page should not arm a destructive
 * confirmation. So the path appears when the founder asks what would be
 * deleted, and until then this page explains the data root in words.
 *
 * **Amended 2026-10-17 (#373, D30): a learner with an account has their own section.**
 * "Nothing anywhere else. No account, no server" described a one-person install and is
 * false of the pilot, where classmates sign in to the founder's server. Someone who
 * signed in with a username (which the shell has already asked, so this adds no request
 * and still fetches nothing of its own on load) is shown "Download my data" and "Delete
 * my account and data" first - they act on that one person - and the founder-only
 * whole-installation panels after, labelled as such. The launch-only founder, who has
 * no username, sees the page exactly as before: nothing here is about them that was not
 * already true. The old intro is kept for them rather than deleted; it is accurate for
 * the install it was written for.
 *
 * **Amended 2026-11-04 (UX round 3): the whole-installation panels are the founder's.**
 * They were shown to every signed-in account while the server refuses them to anyone but the
 * founder (`founder_only`), so a classmate saw controls that did nothing. They now show only
 * when the session says `founder`, or when there is no account at all (the launch-only
 * founder, whose page is unchanged). An account holder who is not the founder sees neither
 * panel nor the "whole installation" heading.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DeleteAllDataResponse, LearnerDeletionResponse } from '@davinci/contracts';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { DeletionPanel } from './deletion-panel.js';
import { DeletionReceipt } from './deletion-receipt.js';
import { ExportPanel } from './export-panel.js';
import { MyDataView } from './my-data-view.js';
import { MyDeletionPanel } from './my-deletion-panel.js';
import { MyDeletionReceipt } from './my-deletion-receipt.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    title: 'My data',
    introAccount:
      'DaVinci keeps what you do on the server you signed in to. You can see all of your own data on this page, or delete your account and everything of yours, whenever you like \u2014 including if you have withdrawn your consent.',
    introLocal:
      'DaVinci keeps your study records in one folder on this machine. Your answers, the help you ask for and the pages you upload are sent to AI services run by outside companies, as the Consent page says. An export is a copy of that folder, and a deletion is its removal.',
    noteLocal:
      'Your study records are kept in that folder and not in this browser, so closing the tab loses nothing. What is sent to the outside AI services, and to which companies, is on the Consent page.',
    wholeInstallation: 'The whole installation',
    founderOnly:
      'These two act on every learner on this install, so not every account can use them.',
  },
  he: {
    title: 'הנתונים שלי',
    introAccount:
      'DaVinci שומר את הפעילות שלך בשרת שאליו התחברת. אפשר לראות בעמוד הזה את כל הנתונים שלך, או למחוק את החשבון שלך ואת כל מה ששייך לך, בכל עת \u2014 גם אם ביטלת את ההסכמה שלך.',
    introLocal:
      'DaVinci שומר את רשומות הלמידה שלך בתיקייה אחת במכשיר הזה. התשובות שלך, בקשות העזרה והעמודים שאתה מעלה נשלחים לשירותי בינה מלאכותית של חברות חיצוניות, כפי שכתוב בעמוד ההסכמה. ייצוא הוא עותק של התיקייה, ומחיקה היא הסרה שלה.',
    noteLocal:
      'רשומות הלמידה שלך נשמרות בתיקייה הזו ולא בדפדפן, ולכן סגירת הלשונית לא מאבדת דבר. מה נשלח לשירותי הבינה המלאכותית החיצוניים, ולאילו חברות, כתוב בעמוד ההסכמה.',
    wholeInstallation: 'ההתקנה כולה',
    founderOnly: 'שני אלה פועלים על כל הלומדים בהתקנה הזו, ולכן לא כל חשבון יכול להשתמש בהם.',
  },
});

export function DataPage(): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const [receipt, setReceipt] = useState<DeleteAllDataResponse | undefined>(undefined);
  const [myReceipt, setMyReceipt] = useState<LearnerDeletionResponse | undefined>(undefined);

  // The same query, with the same key, as the shell's account control - answered
  // from its cache, so this screen still asks the server for nothing of its own.
  const session = useQuery({ queryKey: queryKeys.authSession, queryFn: () => api.authSession() });
  const hasAccount = session.isSuccess && session.data.username !== null;
  // The whole-installation panels act on every learner and the server refuses them to anyone but the founder
  // (`founder_only`). An account holder who is not the founder never sees them; a launch-only founder (no username)
  // sees the page as before.
  // Before the session answers the page is the one it always was; the existing screen and its tests rely on that.
  const showInstallation = !session.isSuccess || session.data.username === null || session.data.founder;

  if (myReceipt !== undefined) {
    return (
      <div className="page">
        <h2>{t.title}</h2>
        <MyDeletionReceipt receipt={myReceipt} />
      </div>
    );
  }

  // Once the deletion has happened, the whole screen is replaced. Not because
  // the panels would look untidy beside a receipt, but because they would be
  // lying: the export button would offer an archive of an installation that no
  // longer exists, against a server that has stopped answering.
  if (receipt !== undefined) {
    return (
      <div className="page">
        <h2>{t.title}</h2>
        <DeletionReceipt receipt={receipt} />
      </div>
    );
  }

  // One tree whatever the session says. The founder's panels sit at fixed positions
  // under the heading, so when the session query resolves and an account's own section
  // appears ABOVE them, React keeps the panels (and a preview the founder has already
  // asked for) rather than remounting them. Conditionals that render nothing are what
  // hold the positions; an early return per case would have thrown the state away.
  return (
    <div className="page">
      <h2>{t.title}</h2>
      {hasAccount ? (
        <p>{t.introAccount}</p>
      ) : (
        <>
          <p>{t.introLocal}</p>
          <p className="page-note">{t.noteLocal}</p>
        </>
      )}

      {hasAccount ? (
        <>
          <MyDataView />
          <MyDeletionPanel onDeleted={setMyReceipt} />
        </>
      ) : null}
      {hasAccount && showInstallation ? (
        <>
          <h2>{t.wholeInstallation}</h2>
          <p className="page-note">{t.founderOnly}</p>
        </>
      ) : null}

      {showInstallation ? <ExportPanel /> : null}
      {showInstallation ? <DeletionPanel onDeleted={setReceipt} /> : null}
    </div>
  );
}
